/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
import ldFindLastIndex from "lodash-es/findLastIndex";
import {
    ChangeDetectorRef,
    Component,
    DoCheck,
    ElementRef,
    EventEmitter,
    forwardRef,
    inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild
} from "@angular/core";
import { EMPTY, merge, Observable, Subject, Subscription } from "rxjs";
import { auditTime, takeUntil } from "rxjs/operators";

import { LgSimpleChanges } from "@logex/framework/types";
import { LgTranslateService } from "@logex/framework/lg-localization";
import { IOrderByPerLevelSpecification } from "@logex/framework/lg-pivot";
import { LgObserveSizeService, SizeEvent } from "@logex/framework/ui-core";
import { observeOnZone } from "@logex/framework/utilities";
import { LgPivotTableBodyComponent } from "./lg-pivot-table-body.component";
import {
    ILevelExpander,
    IPivotTableLevelHeader,
    IPivotTableLevelHeaderDefinition,
    LgPivotTableLevelExpander
} from "./types";
import { convertPivotLevelHeaders } from "./helpers/convertPivotLevelHeaders";

@Component({
    standalone: false,
    selector: "lg-pivot-table-body-level-headers",
    templateUrl: "./lg-pivot-table-body-level-headers.component.html",
    viewProviders: [
        {
            provide: LgPivotTableLevelExpander,
            useExisting: forwardRef(() => LgPivotTableBodyLevelHeadersComponent)
        }
    ],
    host: {
        class: "table__column--header-levels",
        "[class.table__column--header-levels--compact]": "!_areLabelsVisible"
    }
})
// eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
export class LgPivotTableBodyLevelHeadersComponent
    implements OnInit, DoCheck, OnChanges, OnDestroy, ILevelExpander
{
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _elementRef = inject(ElementRef);
    private _lgTranslate = inject(LgTranslateService);
    private _renderer = inject(Renderer2);
    private _observeSize = inject(LgObserveSizeService);
    private _ngZone = inject(NgZone);

    /**
     * Pivot table body component (required).
     */
    @Input({ required: true }) body!: LgPivotTableBodyComponent;

    /**
     * Levels header definition (required).
     */
    @Input({ required: true }) levels!: IPivotTableLevelHeaderDefinition;

    /**
     * Sorting specification varied per level. This is an array of per-level specifications.
     */
    @Input() sortBy: IOrderByPerLevelSpecification = [];

    @Output() readonly sortByChange = new EventEmitter<IOrderByPerLevelSpecification>();

    @ViewChild("hiddenLevelsWrapper", { static: true }) _hiddenLevelsWrapper!: ElementRef;

    _offset = 0;
    _maxVisibleLevel = 0;
    _empty = false;
    _hoverLevel: number | null = null;
    _lastIndex = 0;
    _areLabelsVisible = true;
    _levelsWidth = 0;
    _convertedLevels: Array<IPivotTableLevelHeader | undefined> = [];

    private _width = 0;
    private _destroyed$ = new Subject<void>();
    private _subscription: Subscription | null = null;
    private _expandActionLabels: { expand: string; collapse: string } | null = null;

    ngOnInit(): void {
        const parent = this._elementRef.nativeElement.parentElement;

        // Width can come from this element or its parent; the hidden wrapper gives required label width.
        // Resize is observed outside Angular zone and batched, so one resize triggers one CD run.
        merge(
            this._observeWidth(this._elementRef),
            parent ? this._observeWidth(new ElementRef(parent)) : EMPTY,
            this._observeWidth(this._hiddenLevelsWrapper)
        )
            .pipe(auditTime(0), observeOnZone(this._ngZone), takeUntil(this._destroyed$))
            .subscribe(() => {
                this._onLevelsChange();
                this._onViewPortChange();
                this._changeDetectorRef.markForCheck();
            });
    }

    getAllExpanded(level: number): boolean {
        return this.body && this.body.getAllExpanded(level);
    }

    toggleLevel(level: number, expanded?: boolean): void {
        if (!this.body) return;

        const expand = expanded ?? !this.getAllExpanded(level);
        if (expand) {
            // Expand any collapsed levels above, otherwise the change wouldn't be visible
            for (let i = this._maxVisibleLevel; i < level; ++i) {
                this.body.toggleLevel(i, true);
            }
        }
        this.body.toggleLevel(level, expand);
    }

    ngDoCheck(): void {
        if (this.body) {
            const offset = this.body.definition ? this.body.definition.$levelIndex : 0;
            const maxVisibleLevel = this.body.maxVisibleLevel;
            const empty = this.body.filteredData == null;

            if (
                offset !== this._offset ||
                maxVisibleLevel !== this._maxVisibleLevel ||
                empty !== this._empty
            ) {
                this._offset = offset;
                this._maxVisibleLevel = maxVisibleLevel;
                this._empty = empty;
                this._changeDetectorRef.markForCheck();
            }
        }
    }

    ngOnChanges(changes: LgSimpleChanges<LgPivotTableBodyLevelHeadersComponent>): void {
        if (changes.body) {
            this._unsubscribe();
            if (this.body) {
                this._subscription = this.body
                    ._hoverLevelObservable()
                    .pipe(takeUntil(this._destroyed$))
                    .subscribe(level => {
                        this._hoverLevel = level;
                    });
            }
        }
        if (changes.levels) {
            this._convertLevels();
        }
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    _sortByDisabled(
        levelDef: IPivotTableLevelHeader,
        _isTheVisibleRow: boolean,
        index: number
    ): boolean {
        return !levelDef.orderBy || this._maxVisibleLevel < index || this._empty;
    }

    _levelLabelVisible(isTheVisibleRow: boolean, index: number): boolean {
        return !isTheVisibleRow || this._areLabelsVisible || this._maxVisibleLevel === index;
    }

    _expandTooltip(index: number): string {
        const levelDef = this._convertedLevels[index];

        if (!levelDef) {
            return "";
        }

        const expanded = this.getAllExpanded(index);
        const action = this._getExpandActionLabel(expanded);

        return levelDef.header ? `${action} ${levelDef.header}` : action;
    }

    _showDotForLastLevel(isTheVisibleRow: boolean, index: number): boolean {
        return isTheVisibleRow && !this._areLabelsVisible && index === this._lastIndex;
    }

    _toggleNameLevel(level: number, $event: MouseEvent): void {
        $event.stopPropagation();
        $event.preventDefault();

        if (!this.body || this._empty) return;

        const last = level === this._lastIndex;
        if (this._maxVisibleLevel < level) {
            for (let i = this._maxVisibleLevel; i < level; ++i) {
                this.body.toggleLevel(i, true);
            }
        }
        if (!last) {
            this.body.toggleLevel(level);
        }
    }

    private _observeWidth(elementRef: ElementRef): Observable<SizeEvent> {
        return this._observeSize
            .observe(elementRef, this._renderer)
            .change({ type: "width", auditTime: 0, outsideZone: true });
    }

    private _getExpandActionLabel(expanded: boolean): string {
        if (this._expandActionLabels == null) {
            this._expandActionLabels = {
                expand: this._lgTranslate.translate("FW._Common.Expand"),
                collapse: this._lgTranslate.translate("FW._Common.Collapse")
            };
        }

        return expanded ? this._expandActionLabels.collapse : this._expandActionLabels.expand;
    }

    private _convertLevels(): void {
        this._expandActionLabels = null;
        this._convertedLevels = convertPivotLevelHeaders(this.levels, this._lgTranslate);
        this._lastIndex = ldFindLastIndex(this._convertedLevels, l => !!l);
    }

    private _onLevelsChange(): void {
        if (this._hiddenLevelsWrapper) {
            const newWidth = this._hiddenLevelsWrapper.nativeElement.clientWidth;

            if (newWidth !== this._levelsWidth) {
                this._levelsWidth = newWidth;
                this._setLabelsVisibility();
            }
        }
    }

    private _onViewPortChange(): void {
        if (this._elementRef.nativeElement) {
            const width =
                this._elementRef.nativeElement.style.width === ""
                    ? this._elementRef.nativeElement.parentElement.clientWidth
                    : this._elementRef.nativeElement.clientWidth;

            if (width !== this._width) {
                this._width = width;
                this._setLabelsVisibility();
            }
        }
    }

    private _setLabelsVisibility(): void {
        if (!this._levelsWidth || !this._width) return;

        this._areLabelsVisible = this._levelsWidth < this._width;
    }

    private _unsubscribe(): void {
        if (this._subscription) {
            this._subscription.unsubscribe();
            this._subscription = null;
        }
    }
}
